/*
 * OrderLineForm.tsx : Formulaire d'ajout ou de modification d'une ligne de commande fournisseur.
 *
 * Structure en CardSection :
 *   - Informations générales : commande (readonly), version (readonly), service, produit
 *   - Identification         : libellé, description
 *   - Type d'imputation      : expenseType + affaire OU centre de coûts (conditionnel) + code comptable (readonly calculé)
 *   - Prix                   : quantité, PU HT, total HT (calculé), total TTC (calculé), taxe
 *   - Livraison              : date prévue, date effective, pourcentage livré
 */

// Import React
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import FontAwesome
import {
	faBox,
	faTruck,
	faEuroSign,
	faTag,
	faDiagramProject,
} from '@fortawesome/free-solid-svg-icons';

// Import Layout
import CardHeader from 'layout/CardHeader';
import CardSection from 'layout/CardSection';

// Import Communs
import {
	InputField,
	SelectField,
	DatePickerField,
	finishFormSave,
	FormSaveActions,
	formatLocalDate,
	parseLocalDate,
	useFormDirty,
} from '../../../common';
import WorkflowLockBanner from '../../../common/components/WorkflowLockBanner/WorkflowLockBanner';

// Import Commande
import { OrderLineData } from '../types/types';
import { orderLineApi } from '../services/orderLineApi';

// Import Taxes, produits et services
import { taxRatesApi } from '../../taxRate/services/taxRateApi';
import { ProductData, productsApi, TaxRateData } from 'features/products';
import { ServiceData, servicesApi } from 'features/services';
import { orderVersionsApi } from '../services/orderVersionsApi';

// Import Centres de coûts et catégories
import { CostCenterData, costCentersApi } from 'features/costCenter';
import {
	CostCenterCategoryData,
	costCenterCategoriesApi,
} from 'features/costCenterCategories';

// Import Affaires (projets)
import { projectsApi } from 'features/projects';

// Composant OrderLineForm
const OrderLineForm: React.FC = () => {
	// ─── State de chargement ────────────────────────────────────────────────────
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	// ─── State du formulaire ────────────────────────────────────────────────────
	const [formData, setFormData] = React.useState<OrderLineData>({
		id: 0,
		label: '',
		deliveryDate: '',
		expenseType: '',
		accountingCode: '',
		description: '',
		unit: '',
		quantity: 1.0,
		unitPrice: 0.0,
		totalExclTax: 0.0,
		project: null,
		costCenter: null,
		service: undefined,
		product: undefined,
		taxRate: {
			code: '',
			label: '',
			rate: 0.0,
			country: 'FR',
			isActive: true,
		},
		orderVersion: {
			id: 0,
			label: '',
			versionIndex: 0,
			orderLines: [],
			businessOrder: { id: 0, referenceNumber: '', orderVersions: [] },
		},
	});

	// ─── Navigation et paramètres URL ──────────────────────────────────────────
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { isDirty, markClean } = useFormDirty(formData, baselineReady);

	// ─── Données de référence : taxes, produits, services ──────────────────────
	const [taxRates, setTaxRates] = useState<TaxRateData[]>([]);
	const [products, setProducts] = useState<ProductData[]>([]);
	const [services, setServices] = useState<ServiceData[]>([]);

	const taxRateOptions = taxRates.map((tr) => ({
		key: tr.code,
		label: tr.label || '',
	}));
	const productOptions = products.map((p) => ({
		key: p.id.toString(),
		label: p.description || '',
	}));
	const serviceOptions = services.map((s) => ({
		key: s.id.toString(),
		label: s.description || '',
	}));

	// ─── Données de référence : centres de coûts ───────────────────────────────
	const [allCostCenters, setAllCostCenters] = useState<CostCenterData[]>([]);
	const [costCenterCategories, setCostCenterCategories] = useState<
		CostCenterCategoryData[]
	>([]);
	const [loadingCostCenters, setLoadingCostCenters] = useState(false);
	// State dédié pour la catégorie filtrante (indépendant du CC actuellement sélectionné)
	const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
		null
	);

	const costCenterCategoryOptions = costCenterCategories.map((cat) => ({
		key: cat.id.toString(),
		label: cat.label || '',
	}));
	// Filtre les CCs par catégorie sélectionnée via le state dédié (et non via formData)
	const costCenterOptions = allCostCenters
		.filter(
			(cc) => !selectedCategoryId || cc.categorie?.id === selectedCategoryId
		)
		.map((cc) => ({ key: cc.id.toString(), label: cc.label || '' }));

	// ─── Données de référence : affaires (projets) ─────────────────────────────
	const [projectOptions, setProjectOptions] = useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingProjects, setLoadingProjects] = useState(false);

	// ─── Options statiques du type d'imputation ────────────────────────────────
	const expenseTypeOptions: { key: string; label: string }[] = [
		{ key: 'PROJECT', label: 'Affaire' },
		{ key: 'OPERATIONAL', label: 'Exploitation' },
		{ key: 'CAPITAL', label: 'Investissement' },
	];

	// ─── Chargement initial ─────────────────────────────────────────────────────
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		// Chargement des données de référence communes
		taxRatesApi
			.listPaginated(token, { limit: 999999 })
			.then((res) => setTaxRates(res.data))
			.catch((err) => console.error('Erreur chargement taux de taxe :', err));

		productsApi
			.listPaginated(token, { limit: 999999 })
			.then((res) => setProducts(res.data))
			.catch((err) => console.error('Erreur chargement produits :', err));

		servicesApi
			.listPaginated(token, { limit: 999999 })
			.then((res) => setServices(res.data))
			.catch((err) => console.error('Erreur chargement services :', err));

		// Lazy loading des centres de coûts et leurs catégories
		setLoadingCostCenters(true);
		Promise.all([
			costCentersApi.listPaginated(token, { limit: 999999 }),
			costCenterCategoriesApi.listSelect(token),
		])
			.then(([ccRes, catItems]) => {
				setAllCostCenters(ccRes.data);
				setCostCenterCategories(
					catItems.map((item) => ({
						id: item.id,
						label: String(item.label),
					}))
				);
			})
			.catch((err) =>
				console.error('Erreur chargement centres de coûts :', err)
			)
			.finally(() => setLoadingCostCenters(false));

		// Lazy loading des affaires (projets) — route /select allégée
		setLoadingProjects(true);
		projectsApi
			.listSelect(token)
			.then((items) =>
				setProjectOptions(
					items.map((item) => ({
						key: item.id.toString(),
						label: String(item.referenceNumber),
					}))
				)
			)
			.catch((err) => console.error('Erreur chargement affaires :', err))
			.finally(() => setLoadingProjects(false));

		// Chargement de la ligne de commande si édition
		if (id) {
			orderLineApi
				.get(Number(id), token)
				.then((data: OrderLineData) => {
					setFormData(data);
					// Initialise le filtre catégorie depuis le CC chargé, si présent
					if (data.costCenter?.categorie?.id) {
						setSelectedCategoryId(data.costCenter.categorie.id);
					}
				})
				.catch((err) =>
					console.error('Erreur chargement ligne de commande :', err)
				)
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// Création : pré-remplissage de la version de commande depuis l'URL
			const urlParams = new URLSearchParams(window.location.search);
			const orderVersionId = urlParams.get('orderVersionId');
			if (orderVersionId) {
				orderVersionsApi
					.get(Number(orderVersionId), token)
					.then((data) =>
						setFormData((prev) => ({ ...prev, orderVersion: data }))
					)
					.catch((err) =>
						console.error('Erreur chargement version de commande :', err)
					);
			}
			setLoading(false);
			setBaselineReady(true);
		}
	}, [id]);

	// ─── Pré-sélection TVA par défaut (TVA_FR_20) lors d'une création ──────────
	React.useEffect(() => {
		if (!id && taxRates.length > 0) {
			setFormData((prev) => {
				const needsDefault =
					!prev.taxRate?.code || prev.taxRate.code.trim() === '';
				if (!needsDefault) return prev;
				const defaultTax = taxRates.find((tr) => tr.code === 'TVA_FR_20');
				return { ...prev, taxRate: defaultTax ?? prev.taxRate };
			});
		}
	}, [taxRates, id]);

	// ─── Soumission du formulaire ───────────────────────────────────────────────
	const handleSave = (shouldLeave: boolean) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		const redirectPath = `/app/orderVersions/${formData.orderVersion?.id ?? ''}/manage`;
		if (id) {
			orderLineApi
				.update(Number(id), formData, token)
				.then(() =>
					finishFormSave(shouldLeave, {
						onLeave: () => navigate(redirectPath),
						markClean,
					})
				)
				.catch((err) =>
					console.error('Erreur mise à jour ligne de commande :', err)
				)
				.finally(() => setIsSubmitting(false));
		} else {
			orderLineApi
				.create(formData, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave: () => navigate(redirectPath),
						markClean,
						stayOnEdit: created?.id
							? () =>
									navigate(`/app/orderLines/${created.id}/edit`, {
										replace: true,
									})
							: undefined,
					})
				)
				.catch((err) =>
					console.error('Erreur création ligne de commande :', err)
				)
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		handleSave(true);
	};

	// ─── Gestion des champs texte/number ───────────────────────────────────────
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		const updated = { ...formData, [name]: value };
		// Recalcul automatique du total HT à chaque modification de quantité ou prix unitaire
		if (name === 'unitPrice' || name === 'quantity') {
			updated.totalExclTax = (updated.unitPrice || 0) * (updated.quantity || 0);
		}
		setFormData(updated);
	};

	// ─── Gestion des selects ────────────────────────────────────────────────────
	const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		// Type d'imputation : vide l'affaire et/ou le CC selon le nouveau type sélectionné
		if (name === 'expenseType') {
			// Quand on change le type d'imputation :
			// - PROJECT  : on vide le CC et on garde le code (sera recalculé si projet selectionné)
			// - OPERATIONAL : on vide le projet et on garde le code (sera recalculé si CC selectionné)
			// - CAPITAL : on vide projet et CC mais on conserve le code pour permettre la saisie manuelle
			setFormData((prev) => ({
				...prev,
				expenseType: value,
				project: value !== 'PROJECT' ? null : prev.project,
				costCenter: value !== 'OPERATIONAL' ? null : prev.costCenter,
				// Ne réinitialise pas le code comptable pour CAPITAL afin qu'on puisse le saisir manuellement
				accountingCode: value === 'CAPITAL' ? prev.accountingCode : '',
			}));
			return;
		}

		// Affaire sélectionnée → force expenseType = PROJECT, vide le CC, calcule le code comptable
		if (name === 'project') {
			const selectedOption = projectOptions.find((opt) => opt.key === value);
			setFormData((prev) => ({
				...prev,
				project: selectedOption
					? {
							id: Number(selectedOption.key),
							referenceNumber: selectedOption.label,
						}
					: null,
				expenseType: 'PROJECT',
				costCenter: null,
				accountingCode: selectedOption?.label ?? '',
			}));
			return;
		}

		// Catégorie de CC sélectionnée → met à jour le filtre, réinitialise la sélection CC courante
		if (name === 'costCenterCategory') {
			const selectedCat = costCenterCategories.find(
				(cat) => String(cat.id) === value
			);
			// Met à jour le state dédié pour le filtre (indépendant du CC sélectionné)
			setSelectedCategoryId(selectedCat?.id ?? null);
			// Réinitialise le CC sélectionné car la catégorie a changé
			setFormData((prev) => ({
				...prev,
				costCenter: null,
			}));
			return;
		}

		// Centre de coûts sélectionné → calcule le code comptable, force expenseType = OPERATIONAL
		if (name === 'costCenter') {
			const selectedCC = allCostCenters.find((cc) => String(cc.id) === value);
			setFormData((prev) => ({
				...prev,
				costCenter: selectedCC
					? {
							...selectedCC,
							categorie: prev.costCenter?.categorie ?? selectedCC.categorie,
						}
					: null,
				expenseType: 'OPERATIONAL',
				project: null,
				accountingCode: selectedCC?.code ?? '',
			}));
			return;
		}

		// Taxe
		if (name === 'taxRate') {
			const taxRate = taxRates.find((tr) => tr.code === value);
			setFormData((prev) => ({ ...prev, taxRate: taxRate ?? prev.taxRate }));
			return;
		}

		// Service → pré-remplit libellé, description, prix, taxe
		if (name === 'service') {
			const service = services.find((s) => String(s.id) === value);
			setFormData((prev) => ({
				...prev,
				service,
				product: undefined,
				label: service?.label ?? prev.label,
				description: service?.description ?? prev.description,
				unitPrice: service?.defaultPrice ?? prev.unitPrice,
				taxRate: service?.defaultTaxRate ?? prev.taxRate,
				totalExclTax:
					(service?.defaultPrice ?? prev.unitPrice ?? 0) * (prev.quantity ?? 0),
			}));
			return;
		}

		// Produit → pré-remplit libellé, description, prix, taxe
		if (name === 'product') {
			const product = products.find((p) => String(p.id) === value);
			setFormData((prev) => ({
				...prev,
				product,
				service: undefined,
				label: product?.label ?? prev.label,
				description: product?.description ?? prev.description,
				unitPrice: product?.defaultPrice ?? prev.unitPrice,
				taxRate: product?.defaultTaxRate ?? prev.taxRate,
				totalExclTax:
					(product?.defaultPrice ?? prev.unitPrice ?? 0) * (prev.quantity ?? 0),
			}));
			return;
		}

		// Mise à jour standard
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	// ─── Rendu ──────────────────────────────────────────────────────────────────
	return (
		<div>
			<div className="max-w-5xl mx-auto p-6 bg-white rounded-lg shadow-md space-y-4">
				{/* En-tête de la card */}
				<CardHeader
					icon={faBox}
					title={
						id
							? "Édition d'une ligne de commande"
							: 'Nouvelle ligne de commande'
					}
					loading={loading}
					createdAt={undefined}
				/>

				<WorkflowLockBanner lock={formData.workflowLock} />
				<form
					onSubmit={handleSubmit}
					className="grid grid-cols-1 md:grid-cols-3 gap-6"
				>
					<fieldset
						disabled={Boolean(formData.workflowLock?.locked)}
						className="contents"
					>
						{/* ── Section : Informations générales ─────────────────────────── */}
						<CardSection title="Informations générales" icon={faTag}>
							{/* Commande parente (lecture seule) */}
							<InputField
								label="Commande"
								name="referenceNumber"
								value={
									formData.orderVersion?.businessOrder?.referenceNumber || ''
								}
								onChange={handleChange}
								loading={loading}
								placeholder="—"
								readOnly
							/>
							{/* Version de commande (lecture seule) */}
							<InputField
								label="Version de commande"
								name="orderVersionLabel"
								value={
									formData.orderVersion?.label
										? `${formData.orderVersion.label} (indice ${formData.orderVersion.versionIndex})`
										: ''
								}
								onChange={handleChange}
								loading={loading}
								placeholder="—"
								readOnly
							/>
							{/* Service lié — pré-remplit libellé / prix / taxe */}
							<SelectField
								label="Service"
								selectedKey={formData.service?.id?.toString() || ''}
								onValueChange={(val) =>
									handleChangeSelect({
										target: { name: 'service', value: val },
									} as React.ChangeEvent<HTMLSelectElement>)
								}
								options={serviceOptions}
								loading={loading}
								entityHref={
									formData.service?.id
										? `/app/settings/services/${formData.service.id}/edit`
										: undefined
								}
								onClear={() =>
									setFormData((prev) => ({
										...prev,
										service: null,
									}))
								}
							/>
							{/* Produit lié — pré-remplit libellé / prix / taxe */}
							<SelectField
								label="Produit"
								selectedKey={formData.product?.id?.toString() || ''}
								onValueChange={(val) =>
									handleChangeSelect({
										target: { name: 'product', value: val },
									} as React.ChangeEvent<HTMLSelectElement>)
								}
								options={productOptions}
								loading={loading}
								entityHref={
									formData.product?.id
										? `/app/settings/products/${formData.product.id}/edit`
										: undefined
								}
								onClear={() =>
									setFormData((prev) => ({
										...prev,
										product: null,
									}))
								}
							/>
						</CardSection>

						{/* ── Section : Identification ──────────────────────────────────── */}
						<CardSection title="Identification" icon={faTag}>
							<InputField
								label="Libellé"
								name="label"
								value={formData.label}
								onChange={handleChange}
								required
								loading={loading}
								placeholder="ex : Prestation d'ingénierie"
							/>
							<InputField
								label="Description"
								name="description"
								value={formData.description ?? ''}
								onChange={handleChange}
								loading={loading}
								placeholder="ex : Détail de la prestation"
							/>
						</CardSection>

						{/* ── Section : Type d'imputation ───────────────────────────────── */}
						<CardSection title="Type d'imputation" icon={faDiagramProject}>
							{/* Type : PROJECT / OPERATIONAL / CAPITAL */}
							<SelectField
								label="Type de dépense"
								selectedKey={formData.expenseType || ''}
								onValueChange={(val) =>
									handleChangeSelect({
										target: { name: 'expenseType', value: val },
									} as React.ChangeEvent<HTMLSelectElement>)
								}
								options={expenseTypeOptions}
								loading={loading}
								required
							/>

							{/* Affaire — visible si expenseType = PROJECT */}
							{formData.expenseType === 'PROJECT' && (
								<SelectField
									label="Affaire"
									selectedKey={formData.project?.id?.toString() || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: { name: 'project', value: val },
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={projectOptions}
									loading={loading}
									optionsLoading={loadingProjects}
									required
									entityHref={
										formData.project?.id
											? `/app/projects/${formData.project.id}/edit`
											: undefined
									}
								/>
							)}

							{/* Centre de coûts — visible si expenseType = OPERATIONAL */}
							{formData.expenseType === 'OPERATIONAL' && (
								<>
									<SelectField
										label="Catégorie de centre de coûts"
										selectedKey={selectedCategoryId?.toString() || ''}
										onValueChange={(val) =>
											handleChangeSelect({
												target: { name: 'costCenterCategory', value: val },
											} as React.ChangeEvent<HTMLSelectElement>)
										}
										options={costCenterCategoryOptions}
										loading={loading}
										optionsLoading={loadingCostCenters}
										entityHref={
											selectedCategoryId
												? `/app/settings/cost-center-categories/${selectedCategoryId}/edit`
												: undefined
										}
										onClear={() => {
											setSelectedCategoryId(null);
											setFormData((prev) => ({
												...prev,
												costCenter: null,
											}));
										}}
									/>
									<SelectField
										label="Centre de coûts"
										selectedKey={formData.costCenter?.id?.toString() || ''}
										onValueChange={(val) =>
											handleChangeSelect({
												target: { name: 'costCenter', value: val },
											} as React.ChangeEvent<HTMLSelectElement>)
										}
										options={costCenterOptions}
										loading={loading}
										optionsLoading={loadingCostCenters}
										required
										entityHref={
											formData.costCenter?.id
												? `/app/settings/cost-centers/${formData.costCenter.id}/edit`
												: undefined
										}
									/>
								</>
							)}

							{/* CAPITAL : pas de champ d'imputation pour l'instant */}

							{/* Code comptable calculé automatiquement à partir de l'affaire ou du CC (lecture seule) */}
							<InputField
								label="Code comptable"
								name="accountingCode"
								value={formData.accountingCode ?? ''}
								onChange={handleChange}
								loading={loading}
								placeholder={
									formData.expenseType === 'CAPITAL'
										? 'saisir le code comptable'
										: 'calculé automatiquement'
								}
								// Editable uniquement pour les investissements (CAPITAL)
								readOnly={formData.expenseType !== 'CAPITAL'}
							/>
						</CardSection>

						{/* ── Section : Prix ────────────────────────────────────────────── */}
						<CardSection title="Prix" icon={faEuroSign}>
							<InputField
								label="Quantité"
								name="quantity"
								type="number"
								value={formData.quantity?.toString() ?? '1'}
								onChange={handleChange}
								required
								loading={loading}
								placeholder="ex : 1.0"
							/>
							<InputField
								label="Prix unitaire (HT)"
								name="unitPrice"
								type="number"
								value={formData.unitPrice?.toString() ?? '0'}
								onChange={handleChange}
								loading={loading}
								placeholder="ex : 1000.00"
							/>
							<InputField
								label="Total HT"
								name="totalExclTax"
								type="number"
								value={formData.totalExclTax?.toString() ?? '0'}
								onChange={handleChange}
								loading={loading}
								placeholder="calculé automatiquement"
								readOnly
							/>
							<InputField
								label="Total TTC"
								name="totalTtc"
								type="number"
								value={(
									(formData.totalExclTax ?? 0) *
									(1 + (formData.taxRate?.rate ?? 0))
								).toFixed(2)}
								onChange={handleChange}
								loading={loading}
								placeholder="calculé automatiquement"
								readOnly
							/>
							{/* Taxe applicable */}
							<SelectField
								label="Taxe"
								selectedKey={formData.taxRate?.code || ''}
								onValueChange={(val) =>
									handleChangeSelect({
										target: { name: 'taxRate', value: val },
									} as React.ChangeEvent<HTMLSelectElement>)
								}
								options={taxRateOptions}
								loading={loading}
								entityHref={
									formData.taxRate?.id
										? `/app/taxrates/${formData.taxRate.id}/edit`
										: undefined
								}
								onClear={() =>
									setFormData((prev) => ({
										...prev,
										taxRate: null,
									}))
								}
							/>
						</CardSection>
					</fieldset>

					{/* ── Section : Livraison ───────────────────────────────────────── */}
					<CardSection title="Livraison" icon={faTruck}>
						<fieldset
							disabled={Boolean(formData.workflowLock?.locked)}
							className="m-0 min-w-0 border-0 p-0"
						>
							<DatePickerField
								label="Date de livraison prévue"
								name="deliveryDate"
								value={parseLocalDate(formData.deliveryDate)}
								onChange={(date) =>
									setFormData({
										...formData,
										deliveryDate: date ? formatLocalDate(date) : '',
									})
								}
								placeholder="Sélectionnez une date"
								loading={loading}
							/>
						</fieldset>
						<DatePickerField
							label="Date de livraison effective"
							name="deliveredAt"
							value={
								formData.deliveredAt
									? new Date(formData.deliveredAt)
									: undefined
							}
							onChange={(date) =>
								setFormData({ ...formData, deliveredAt: date || undefined })
							}
							placeholder="Sélectionnez une date"
							loading={loading}
							readOnly={
								Boolean(formData.workflowLock?.locked) &&
								formData.orderVersion?.status === 'CANCELED'
							}
						/>
						<InputField
							label="Pourcentage livré (%)"
							name="deliveredPercent"
							type="number"
							value={formData.deliveredPercent?.toString() ?? ''}
							onChange={handleChange}
							loading={loading}
							placeholder="ex : 100"
							readOnly={
								Boolean(formData.workflowLock?.locked) &&
								formData.orderVersion?.status === 'CANCELED'
							}
						/>
					</CardSection>
				</form>

				{/* ── Boutons Annuler / Enregistrer ──────────────────────────────── */}
				<FormSaveActions
					isDirty={isDirty}
					isSubmitting={isSubmitting}
					onCancel={() =>
						navigate(
							`/app/orderVersions/${formData.orderVersion?.id ?? ''}/manage`
						)
					}
					onSave={() => handleSave(false)}
					onSaveAndLeave={() => handleSave(true)}
				/>
			</div>
		</div>
	);
};

export default OrderLineForm;
